import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Textarea } from "@/shared/ui";
import { createUserReport } from "@/shared/db";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";

interface ReportErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-filled when opened from ErrorBoundary */
  errorMessage?: string;
  errorStack?: string;
}

const REPORT_TYPES = [
  { value: "error", labelKey: "reportError.types.error" },
  { value: "bug", labelKey: "reportError.types.bug" },
  { value: "feedback", labelKey: "reportError.types.feedback" },
  { value: "suggestion", labelKey: "reportError.types.suggestion" },
] as const;

export const ReportErrorModal: React.FC<ReportErrorModalProps> = ({
  isOpen,
  onClose,
  errorMessage,
  errorStack,
}) => {
  const { t } = useTranslation();
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const [reportType, setReportType] = useState<"error" | "bug" | "feedback" | "suggestion">(
    errorMessage ? "error" : "feedback"
  );
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState(errorMessage || "");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const sub = subject.trim();
    const desc = description.trim();
    if (!sub || !desc) {
      toast.error(t("reportError.errors.required"));
      return;
    }
    if (desc.length < 10) {
      toast.error(t("reportError.errors.minLength"));
      return;
    }
    setSubmitting(true);
    try {
      const result = await createUserReport({
        reportType,
        subject: sub,
        description: desc,
        pageUrl: window.location.href,
        errorMessage: errorMessage || undefined,
        errorStack: errorStack || undefined,
        userId: user?.id || null,
      });
      if (result.success) {
        toast.success(t("reportError.success"));
        setSubject("");
        setDescription("");
        onClose();
      } else {
        toast.error(t("reportError.errors.submitFailed"));
      }
    } catch {
      toast.error(t("reportError.errors.submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSubject("");
      setDescription("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-tiber-900">
                {t("reportError.title")}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="p-2 rounded-lg hover:bg-tiber-100 text-tiber-600 transition-colors"
              aria-label={t("common.close")}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-tiber-600 mb-4">
            {t("reportError.description")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-1">
                {t("reportError.typeLabel")}
              </label>
              <div className="flex flex-wrap gap-2">
                {REPORT_TYPES.map(({ value, labelKey }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setReportType(value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      reportType === value
                        ? "bg-tiber-500 text-white"
                        : "bg-tiber-100 text-tiber-700 hover:bg-tiber-200"
                    }`}
                  >
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label={t("reportError.subjectLabel")}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("reportError.subjectPlaceholder")}
              required
              maxLength={200}
              disabled={submitting}
            />

            <Textarea
              label={t("reportError.descriptionLabel")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("reportError.descriptionPlaceholder")}
              required
              rows={4}
              maxLength={5000}
              disabled={submitting}
            />

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
                className="flex-1"
              >
                {submitting ? t("reportError.sending") : t("reportError.submit")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
